import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal  } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { selectStatusLoading } from '../../../core/redux/status/selectors';
import { MachineService } from '../../../service/api/machine-service';
const { MACHINE_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'machines';
export const INITIAL_STATE = {
  data: [],  
  loading: false,
  error: null,
  machine: null,
  printersdata: [],
  filters: {
    status: {
      value: 'In Machine', 
      label: 'In Machine' 
    },
    searchText: ''
  },  
  filterVisible: false,
  tileView: true,
  selectedMachine: null,
  editModalVisible: false,  
  
};

export const fetchMachines = createAsyncThunk(
  'machines/fetchMachines',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await MachineService.getAllMachines(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Machines');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Machines');
    }
  },
);

export const addMachine = createAsyncThunk(
  'machines/addMachine',
  async (machinePayload, { rejectWithValue }) => {
    try {
      const response = await MachineService.createMachine(machinePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Machines');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Machines');
    }
  },
);

export const editMachine = createAsyncThunk(
  'machines/editMachine',
  async (machinePayload, { rejectWithValue }) => {
    try {
      const response = await MachineService.createMachine(machinePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update machine');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update machine');
    }
  },
);

export const downloadMachines = createAsyncThunk(
  'machines/downloadMachines',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await MachineService.downloadMachine(filters);
      const blob = response;
      saveAs(blob, `Machines.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Machines');
    }
  },
);

export const deleteMachine = createAsyncThunk(
  'machines/deleteMachine',
  async (machineId, { rejectWithValue }) => {
    try {
      const response = await MachineService.deleteMachine(machineId);
      if (response.status === 200) {
        return machineId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete machine');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete machine');
    }
  },
);

export const fetchMachinesWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    fetchMachines({
      searchText: filters.searchText,
      id:0
    }),
  );
};

export const downloadMachinesWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    downloadMachines({
      searchText: filters.searchText,
      machine: filters?.machine?.value,
      status: filters?.status?.value,
      machineDate: filters?.shipDate,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'machines',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setMachine:(state, action) => {
      state.machine = action.payload;
    },
    resetFilters: (state) => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setMachine: (state, action) => {
      state.filters.machine = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },     
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMachines.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addMachine.fulfilled, (state, action) => {
        const newMachine = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newMachine, ...state.data];
      })
      .addCase(addMachine.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editMachine.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editMachine.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteMachine.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addMachine.rejected, editMachine.rejected
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addMachine.fulfilled, editMachine.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(MACHINE_ADD_EDIT));
    listenerApi.dispatch(fetchMachinesWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Machine saved successfully',
        type: 'success',
      }),
    );
  },
});


redux.listener.startListening({
  matcher: isAnyOf(editMachine.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(MACHINE_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Machine updated successfully',
        type: 'success',
      }),
    );
  },
});
redux.listener.startListening({
  matcher: isAnyOf(addMachine.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(MACHINE_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Machine saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteMachine.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Machine deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
